import { isBefore, max, subMinutes } from "date-fns";
import { last, uniq } from "lodash";

import { RootState } from "shared/types";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import {
  getClassStartTime,
  getCurrentlyPresentUserCount,
  getOncePresentUserCount,
  nAttendees,
} from "shared/utils/monitoring";
import { stringComparatorGenerator } from "utils/string";
import { getRoleAndNameForSorting, longDisplayName } from "utils/user";

export const selectCurrentUser = () => (state: RootState) =>
  state.classroom.client.users[state.local.currentUserId];

export const selectUserDropdownOptions = () => (state: RootState) => {
  const allUsers = state.classroom.client.users;
  const users = uniq(
    Object.values(state.monitoring.monitoring_events)
      .map((event) => event.userId)
      // It is possible for users that are not part of the class to have monitoring events.
      // For example, when a user joins the class, we initialize the websocket connections and create monitoring events before
      // knowing whether the user is part of the class. In this case, we should ignore the monitoring events.
      .filter((userId) => userId in allUsers),
  ).map((userId) => allUsers[userId]);
  users.sort(stringComparatorGenerator(longDisplayName));
  return users.map((user) => ({
    value: user.id,
    label: longDisplayName(user),
  }));
};

export const selectNAttendees = () => (state: RootState) => {
  const classInfo = state.classroom.client.info;
  const currentlyPresentUserCount = getCurrentlyPresentUserCount(state.classroom.client);
  const oncePresentUserCount = getOncePresentUserCount(state.classroom.client, state.monitoring);
  return nAttendees(classInfo, currentlyPresentUserCount, oncePresentUserCount);
};

export const selectAllClassUserIds = (filterValue: string[]) => (state: RootState) => {
  // select all users once present in the class and all users who are not observers even if they have not been present
  const users = state.classroom.client.users;
  const oncePresentUserIds = Object.values(state.monitoring.monitoring_events).map(
    (event) => event.userId,
  );
  return Object.values(users)
    .filter((user) => {
      if (user.role === "observer" || user.role === "") {
        if (!filterValue.includes("OBSERVERS") || !oncePresentUserIds.includes(user.id))
          return false;
      } else {
        if (!filterValue.includes("PRESENT") && oncePresentUserIds.includes(user.id)) return false;
        if (!filterValue.includes("ABSENT") && !oncePresentUserIds.includes(user.id)) return false;
      }
      return true;
    })
    .sort(stringComparatorGenerator(getRoleAndNameForSorting))
    .map((user) => user.id);
};
